import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useApp } from '../context/AppContext';
import { useLoading } from '../context/LoadingContext';
import { useToast } from '../context/ToastContext';
import { simulateNetworkDelay } from '../utils/async';
import { decodeJwtPayload } from '../utils/jwt';
import tuxLogo from '../assets/tux-logo.png';

// Real Google Sign-In (Handoff §2/§5). The credential Google hands back is a
// JWT — we decode (not verify) it here just to read the email and drive the
// UI. Real signature verification belongs server-side, in the future Apps
// Script endpoint, per the handoff.
export default function Login() {
  const { signInWithGmail, setIdToken, dataLoaded, dataError } = useApp();
  const { withLoading } = useLoading();
  const { showError } = useToast();
  const navigate = useNavigate();

  const completeSignIn = async (gmail) => {
    if (!gmail || !gmail.trim()) return;
    const match = await withLoading(async () => {
      await simulateNetworkDelay();
      return signInWithGmail(gmail);
    });
    if (!match) {
      showError("Account not recognized — that Gmail address isn't on the team list.");
      return;
    }
    navigate(match.isAdmin ? '/admin' : '/checkin');
  };

  const handleGoogleSuccess = (credentialResponse) => {
    const payload = decodeJwtPayload(credentialResponse.credential);
    setIdToken(credentialResponse.credential);
    completeSignIn(payload.email);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-light-gold px-4">
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-sm p-8">
        <div className="text-center mb-8">
          <img src={tuxLogo} alt="TUX Leads" className="w-14 h-14 rounded-xl mx-auto mb-3" />
          <h1 className="text-2xl font-semibold text-ink">TrackWithTux</h1>
          <p className="mt-2 text-sm text-muted">
            We match your Gmail to the team list — no separate account needed.
          </p>
        </div>

        {dataLoaded && dataError && (
          <div className="mb-4 rounded-lg bg-error-bg text-error-tx text-sm px-3 py-2">
            Couldn't load the team sheet — {dataError}
          </div>
        )}

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => showError('Google sign-in failed — please try again.')}
          />
        </div>
      </div>
    </div>
  );
}
